import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import clsx from "clsx";
import { useFormik } from "formik";
import { setUser } from "../redux/AuthSlice";
import { useApolloClient, useLazyQuery } from "@apollo/client";
import { GET_TOKEN, SIGN_UP_INVITATION } from "src/setup/apollo/queries";

const SUBSCRIPTION_KEY = "PqBjJdOTjJwGPVHVqoMqsKYQ4AK3rYk417XvxC1U6OPb9NHnxXDMhVgP49H2Icyt";

const loginSchema = Yup.object().shape({
  username: Yup.string()
    .min(3, "Minimum 3 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Username is required"),
  password: Yup.string()
    .min(3, "Minimum 3 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Password is required"),
});

const emailSchema = Yup.object().shape({
  email: Yup.string()
    .email("Geçerli bir e-posta adresi giriniz")
    .required("E-posta adresi gereklidir"),
});

const initialValues = {
  username: "",
  password: "",
};

const initialEmailValues = {
  email: "",
};

export function Login() {
  const [loading, setLoading] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitationSuccess, setInvitationSuccess] = useState(false);
  const dispatch = useDispatch();
  const client = useApolloClient();
  
  const emailFormik = useFormik({
    initialValues: initialEmailValues,
    validationSchema: emailSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setInvitationLoading(true);
      setInvitationSuccess(false);
      try {
        const response = await client.query({
          query: SIGN_UP_INVITATION,
          variables: {
            email: values.email,
            subscriptionKey: SUBSCRIPTION_KEY,
          },
        });

        if (response?.data?.signUpInvitation?.status === "SUCCESS") {
          setInvitationSuccess(true);
          setStatus("");
          emailFormik.resetForm();
        } else {
          setStatus(
            response?.data?.signUpInvitation?.exceptionMessage ||
              "Bir hata oluştu. Lütfen tekrar deneyiniz."
          );
        }
      } catch (error) {
        setStatus("Bir hata oluştu. Lütfen tekrar deneyiniz.");
      } finally {
        setInvitationLoading(false);
        setSubmitting(false);
      }
    },
  });

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      setTimeout(async () => {
        var username = values.username;
        var password = values.password;

        var resp = await client
          .query({
            query: GET_TOKEN,
            variables: { username, password },
          })
          .then(async (data) => {
            setLoading(false);
            if (data != null) {
              if (data.data.login.response == "INVALID_CREDENTIALS") {
                setLoading(false);
                setSubmitting(false);
                setStatus("Kullanıcı adı veya şifre hatalı.");
              } else if (
                data.data.login.response == "SALESPERSON_IS_NOT_ACTIVE"
              ) {
                setLoading(false);
                setSubmitting(false);
                setStatus(
                  "Kullanıcınız aktif değil. Lütfen yöneticinize başvurun."
                );
              } else {
                dispatch(
                  setUser({
                    salesPersonId: data.data.login.userInfo.salesPersonId,
                    salesPersonGuid: data.data.login.userInfo.salesPersonGuid,
                    salesPersonName: data.data.login.userInfo.salesPersonName,
                    token: data.data.login.response,
                    serverUrl: "",
                    userData: data.data.login.userInfo,
                  })
                );
                document.location.reload();
              }
            } else {
              setLoading(false);
              setSubmitting(false);
              setStatus("Kullanıcı adı veya şifrenizi kontrol ediniz.");
            }
          })
          .catch((error) => {
            setLoading(false);
            setSubmitting(false);
            setStatus("Kullanıcı adı veya şifrenizi kontrol ediniz.");
          });
      }, 1000);
    },
  });

  if (showSignUp) {
    return (
      <form
        className="form"
        onSubmit={emailFormik.handleSubmit}
        noValidate
        id="signup_invitation_form"
      >
        {/* begin::Heading */}
        <div className="text-center mb-5 d-flex items-center justify-content-center flex-column">
          <h3 className="text-dark mb-3">SABAK</h3>
          <h3 className="text-dark mb-3">Hesap Oluştur</h3>
        </div>
        {/* begin::Heading */}

        {invitationSuccess ? (
          <div className="mb-lg-15 alert alert-success">
            <div className="alert-text font-weight-bold">
              Kayıt davetiyesi e-posta adresinize gönderildi. Lütfen e-postanızı kontrol ediniz.
            </div>
          </div>
        ) : emailFormik.status ? (
          <div className="mb-lg-15 alert alert-danger">
            <div className="alert-text font-weight-bold">{emailFormik.status}</div>
          </div>
        ) : (
          <span></span>
        )}

        {/* begin::Form group */}
        <div className="h-100 mb-10">
          <label className="form-label fs-6 fw-bolder text-dark">E-posta Adresi</label>
          <input
            placeholder="ornek@email.com"
            {...emailFormik.getFieldProps("email")}
            className={clsx(
              "form-control form-control-lg form-control-solid",
              { "is-invalid": emailFormik.touched.email && emailFormik.errors.email },
              {
                "is-valid": emailFormik.touched.email && !emailFormik.errors.email,
              }
            )}
            type="email"
            name="email"
            autoComplete="email"
            disabled={invitationSuccess}
          />
          {emailFormik.touched.email && emailFormik.errors.email && (
            <div className="fv-plugins-message-container">
              <span role="alert">{emailFormik.errors.email}</span>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Action */}
        <div className="text-center">
          <button
            type="submit"
            id="signup_invitation_submit"
            className="btn btn-lg btn-primary w-100 mt-4 mb-3"
            disabled={emailFormik.isSubmitting || !emailFormik.isValid || invitationSuccess}
          >
            {!invitationLoading && (
              <span className="indicator-label">Onay maili gönder</span>
            )}
            {invitationLoading && (
              <span className="indicator-progress" style={{ display: "block" }}>
                Gönderiliyor...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            )}
          </button>
          <button
            type="button"
            className="btn btn-link text-primary w-100"
            onClick={() => {
              setShowSignUp(false);
              setInvitationSuccess(false);
              emailFormik.resetForm();
            }}
          >
            Giriş sayfasına dön
          </button>
        </div>
        {/* end::Action */}
      </form>
    );
  }

  return (
    <form
      className="form"
      onSubmit={formik.handleSubmit}
      noValidate
      id="login_signin_form"
    >
      {/* begin::Heading */}
      <div className="text-center mb-5 d-flex items-center justify-content-center flex-column">
        <h3 className="text-dark mb-3">SABAK</h3>
        <h3 className="text-dark mb-3">Hesabınıza Giriş Yapınız</h3>
      </div>
      {/* begin::Heading */}

      {formik.status ? (
        <div className="mb-lg-15 alert alert-danger">
          <div className="alert-text font-weight-bold">{formik.status}</div>
        </div>
      ) : (
        <span></span>
      )}

      {/* begin::Form group */}
      <div className="h-100 mb-10">
        <label className="form-label fs-6 fw-bolder text-dark">Kullanıcı Adı</label>
        <input
          placeholder="Kullanıcı Adı"
          {...formik.getFieldProps("username")}
          className={clsx(
            "form-control form-control-lg form-control-solid",
            { "is-invalid": formik.touched.username && formik.errors.username },
            {
              "is-valid": formik.touched.username && !formik.errors.username,
            }
          )}
          type="text"
          name="username"
          autoComplete="off"
        />
        {formik.touched.username && formik.errors.username && (
          <div className="fv-plugins-message-container">
            <span role="alert">{formik.errors.username}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className="fv-row mb-3">
        <div className="d-flex justify-content-between mt-n5">
          <div className="d-flex flex-stack mb-2">
            {/* begin::Label */}
            <label className="form-label fw-bolder text-dark fs-6 mb-0">
              Şifre
            </label>
            {/* end::Label */}
          </div>
        </div>
        <input
          type="password"
          placeholder="*******"
          autoComplete="off"
          {...formik.getFieldProps("password")}
          className={clsx(
            "form-control form-control-lg form-control-solid",
            {
              "is-invalid": formik.touched.password && formik.errors.password,
            },
            {
              "is-valid": formik.touched.password && !formik.errors.password,
            }
          )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert">{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div style={{ marginTop: 0 }}>
        <p className="text-center">
          <a
            href="https://rutist.bestera.com.tr/account/forgotpassword?culture=TR"
            target={"_blank"}
            className="text-primary text-decoration-none"
          >
            Şifremi unuttum
          </a>
        </p>
        <p className="text-center mb-0">
          <span className="text-muted">Hesabınız yok mu? </span>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowSignUp(true);
            }}
            className="text-primary text-decoration-none fw-bold"
          >
            Kayıt ol
          </a>
        </p>
      </div>
      <div className="text-center">
        <button
          type="submit"
          id="sign_in_submit"
          className="btn btn-lg btn-primary w-100 mt-4 mb-3"
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className="indicator-label">GİRİŞ</span>}
          {loading && (
            <span className="indicator-progress" style={{ display: "block" }}>
              Lütfen bekleyin...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
    </form>
  );
}
